import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { membershipApi } from "@/api/membershipClient";
import { useOracleMemberSession } from "@/hooks/useOracleMemberSession";
import { useOracleMode } from "@/hooks/useOracleMode";

/**
 * CareLogix™ ORACLE — Public Homepage (Simplicity Release v2.0)
 *
 * Constitutional simplicity:
 *   • No Mongo / LLM / provider diagnostic badges visible to public users.
 *   • 8 intelligence cards (Health · Capital · Finance · Sports · Grants · Research · LifeScore+FoodRx · LOGIX-2).
 *   • 4-tier membership pricing.
 *   • Trust & Safety footer.
 *   • Mobile-first — header < 120px, body ≥ 16px, CTA ≥ 48px tall.
 *
 * Technical content lives on /oracle/platform-map.  AdminOne details live
 * in the operator console. This page exposes ZERO operational telemetry.
 */

const C = {
  bg:       "#0a1a14",
  bg2:      "#0f2820",
  emerald:  "#10b981",
  emeraldD: "#047857",
  gold:     "#facc15",
  goldD:    "#ca8a04",
  ivory:    "#f5f5dc",
  ivoryD:   "#d4d4aa",
  border:   "rgba(16, 185, 129, 0.25)",
};

const S = {
  page: {
    minHeight: "100vh",
    background: `radial-gradient(circle at 50% 0%, ${C.bg2} 0%, ${C.bg} 60%)`,
    color: C.ivory,
    fontFamily: "'Inter', system-ui, sans-serif",
    paddingBottom: 80,
  },
  inner: { maxWidth: 1200, margin: "0 auto", padding: "0 20px" },
  hero: { padding: "48px 20px 24px", maxWidth: 1200, margin: "0 auto", textAlign: "left" },
  kicker: { textTransform: "uppercase", color: C.emerald, fontSize: 11, letterSpacing: 3, fontWeight: 700 },
  h1: { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: "clamp(32px, 6vw, 56px)", margin: "8px 0 4px", lineHeight: 1.1 },
  subtitle: { color: C.ivory, fontSize: "clamp(16px, 2vw, 20px)", margin: "0 0 14px", fontWeight: 600 },
  description: { color: C.ivoryD, fontSize: 16, lineHeight: 1.6, maxWidth: 720, marginBottom: 26 },
  ctaRow: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 },
  ctaPrimary: {
    background: C.gold, color: "#0a0a0a", border: "none",
    padding: "0 22px", height: 48, borderRadius: 8,
    fontSize: 15, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5,
    display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none",
  },
  ctaSecondary: {
    background: "transparent", color: C.ivory,
    border: `1px solid ${C.border}`,
    padding: "0 22px", height: 48, borderRadius: 8,
    fontSize: 15, fontWeight: 600, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none",
  },
  publicBadgeRow: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 },
  publicBadge: {
    fontSize: 10, letterSpacing: 1, padding: "4px 10px", borderRadius: 999,
    background: "rgba(16,185,129,0.10)", color: C.emerald,
    border: "1px solid rgba(16,185,129,0.3)", textTransform: "uppercase", fontWeight: 700,
  },
  publicBadgeMuted: {
    fontSize: 10, letterSpacing: 1, padding: "4px 10px", borderRadius: 999,
    background: "rgba(245,245,220,0.05)", color: C.ivoryD,
    border: "1px solid rgba(245,245,220,0.15)", textTransform: "uppercase", fontWeight: 600,
  },
  sectionTitle: {
    fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 22, margin: 0,
    paddingTop: 8, letterSpacing: 1,
  },
  sectionLead: { color: C.ivoryD, fontSize: 13, marginTop: 4, marginBottom: 20 },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 },
  card: {
    background: "rgba(16,185,129,0.04)", border: `1px solid ${C.border}`,
    borderRadius: 10, padding: "18px 16px", display: "flex", flexDirection: "column", gap: 8,
    minHeight: 170,
  },
  cardIcon: { fontSize: 28, lineHeight: 1, marginBottom: 4 },
  cardTitle: { color: C.ivory, fontSize: 16, fontWeight: 700, margin: 0 },
  cardBody: { color: C.ivoryD, fontSize: 13, lineHeight: 1.5, margin: 0, flex: 1 },
  cardLink: { color: C.gold, fontSize: 12, fontWeight: 700, textDecoration: "none", marginTop: 6 },
  section: { paddingTop: 48, paddingBottom: 8 },
  membershipGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 },
  memCard: {
    background: "rgba(15, 40, 32, 0.55)", border: `1px solid ${C.border}`,
    borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 10,
  },
  memCardFeatured: {
    background: "rgba(250,204,21,0.06)",
    border: `1px solid rgba(250,204,21,0.4)`,
    borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 10,
  },
  memTier: { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 18, margin: 0, letterSpacing: 1 },
  memPrice: { color: C.ivory, fontSize: 24, fontWeight: 700, margin: "0 0 2px" },
  memPriceLine: { color: C.ivoryD, fontSize: 11, margin: 0 },
  memList: { margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 },
  memListItem: { color: C.ivoryD, fontSize: 12, paddingLeft: 14, position: "relative", lineHeight: 1.45 },
  trust: {
    marginTop: 48, padding: "20px 18px", borderRadius: 10,
    background: "rgba(16,185,129,0.06)", border: `1px solid ${C.border}`,
    color: C.ivoryD, fontSize: 13, lineHeight: 1.6, textAlign: "center",
  },
  footer: { marginTop: 40, padding: "14px 20px", textAlign: "center", color: C.ivoryD, fontSize: 11, opacity: 0.7 },
};

const CARDS = [
  { icon: "🏥", title: "Health Intelligence",            body: "Healthcare trends, population health, clinical insights, wellness monitoring, and healthcare opportunity discovery.", to: "/oracle/health" },
  { icon: "💰", title: "Capital Intelligence",           body: "Markets, macroeconomics, business signals, sector analysis, and opportunity monitoring.",                              to: "/oracle/capital" },
  { icon: "📈", title: "Finance & Investment",           body: "Stocks, ETFs, crypto, forex, retirement planning, portfolio research, and watchlists.",                                to: "/oracle/market" },
  { icon: "🎯", title: "Sports Intelligence",            body: "Sports research, performance trends, schedules, analytics, and entertainment intelligence.",                            to: "/oracle/predictive" },
  { icon: "💵", title: "Grants & Funding",               body: "Government grants, foundation funding, private funding opportunities, and eligibility matching.",                       to: "/oracle/grants" },
  { icon: "🔬", title: "Research Intelligence",          body: "Scientific research, publications, innovation tracking, and evidence discovery.",                                       to: "/oracle/research" },
  { icon: "❤️", title: "LifeScore™ + FoodRx™",           body: "Personal wellness scoring, nutrition intelligence, routines, and health optimization.",                                  to: "/oracle/lifescore" },
  { icon: "🤖", title: "LOGIX-2™ Assistant",             body: "Your AI-powered decision-support assistant across every CareLogix intelligence domain.",                                to: "/logix2" },
];

const TIERS = [
  {
    name: "Guest",        price: "Free",   suffix: "Public preview",
    features: ["Public ORACLE Preview", "Limited educational insights", "Compliance Lock™", "No personalization"],
  },
  {
    name: "Professional", price: "$49",    suffix: "per month",
    features: ["LOGIX-2 Briefings", "Saved Watchlists", "LifeScore™ + FoodRx™", "Predictive Intelligence", "Standard PDF Dossier"],
  },
  {
    name: "Executive",    price: "$199",   suffix: "per month", featured: true,
    features: [
      "Everything in Professional",
      "Global Capital Command™",
      "Funding Watch™ + Grants Intelligence™",
      "Opportunity Score™ · Executive Briefings™",
      "Advanced LOGIX-2™ · Weekly Reports",
    ],
  },
  {
    name: "Enterprise",   price: "$999",   suffix: "per month",
    features: [
      "Everything in Executive",
      "Team Workspace · Organization Governance",
      "Audit Exports · Enterprise Dashboards",
      "Custom Workflows",
      "AdminOne Integration Support",
    ],
  },
];

export default function OracleHomePage() {
  const navigate = useNavigate();

  // Phase 42C — Simple Mode is the default. Members can switch to Advanced
  // Mode from the homepage header to reveal provider telemetry, reliability
  // metrics, and the core-satellite topology in deep modules.
  const { mode: uxMode, isSimple, toggleOracleMode } = useOracleMode();

  // Phase 39 — unified member session. Reads token from any legacy key,
  // revalidates against /api/auth/me, and never silently downgrades a
  // signed-in member to the guest funnel.
  const { loading: sessionLoading, member: sessionMember, isAuthenticated } =
    useOracleMemberSession();

  // Phase-3.1 (legacy) — additional /api/membership/me fetch kept for
  // backward compatibility with downstream tests + to enrich the displayed
  // tier label using the canonical tier vocabulary. Strictly additive.
  const [token] = useState(() => {
    try {
      return (
        localStorage.getItem("oracle.session_token") ||
        localStorage.getItem("carelogix_member_token") ||
        localStorage.getItem("oracle_member_token") ||
        ""
      );
    } catch { return ""; }
  });
  const [member, setMember] = useState(null);
  const [memberErr, setMemberErr] = useState(false);
  const [memberLoading, setMemberLoading] = useState(!!token);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setMemberLoading(false); return; }
    setMemberLoading(true);
    membershipApi.me().then((r) => {
      if (cancelled) return;
      setMember(r?.data || null);
      setMemberErr(false);
    }).catch(() => {
      if (cancelled) return;
      setMember(null);
      setMemberErr(true);
    }).finally(() => { if (!cancelled) setMemberLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // A member is authenticated if EITHER source reports a non-guest profile.
  // This protects against the historical bug where the homepage looked only
  // at `oracle.session_token` (never written by the current login flow) and
  // therefore always rendered guest UI for signed-in members.
  const legacyAuth = !!token && !!member && !memberErr;
  const isAuth = isAuthenticated || legacyAuth;

  const displayName = (
    sessionMember?.full_name ||
    member?.user?.name ||
    member?.user?.display_name ||
    member?.user?.email ||
    sessionMember?.email ||
    "Member"
  ).split("@")[0];

  const tierLabel = (
    member?.tier?.label ||
    member?.tier_id ||
    (sessionMember?.tier
      ? sessionMember.tier.charAt(0).toUpperCase() + sessionMember.tier.slice(1)
      : "Member")
  );

  // Show the lightweight loading shim only while BOTH sources are still
  // resolving. Once either confirms auth we render the dashboard.
  const stillChecking = sessionLoading && memberLoading && !isAuth;
  return (
    <main style={S.page} data-testid="oracle-public-homepage">
      {/* Phase 42C — Simple / Advanced Mode toggle, persisted to localStorage. */}
      <button
        type="button"
        onClick={toggleOracleMode}
        data-testid="oracle-mode-toggle"
        data-mode={uxMode}
        style={{
          position: "absolute", top: 14, right: 16, zIndex: 5,
          background: "rgba(13,26,15,0.85)", color: "#facc15",
          border: "1px solid rgba(245,200,66,0.30)",
          padding: "6px 12px", borderRadius: 999, cursor: "pointer",
          fontSize: 11, fontFamily: "'Space Mono', monospace", fontWeight: 700, letterSpacing: "0.1em",
        }}
        aria-pressed={!isSimple}
        aria-label={`Toggle ${isSimple ? "Advanced" : "Simple"} Mode`}
      >
        {isSimple ? "SIMPLE" : "ADVANCED"} MODE · {isSimple ? "switch to Advanced" : "switch to Simple"}
      </button>

      {/* HERO */}
      <header style={S.hero} data-testid="oracle-hero">
        <div style={S.kicker}>CareLogix™</div>
        <h1 style={S.h1}>CareLogix ORACLE™</h1>
        <div style={S.subtitle}>Multi-Domain Intelligence Command™</div>
        <p style={S.description}>
          Health, Capital, Finance, Sports, Grants, Research, Recovery, Governance, and Executive Intelligence — organized through LOGIX-2™.
        </p>

        {/* ── Auth in-flight skeleton — prevents guest UI flash for signed-in members ── */}
        {stillChecking && (
          <div data-testid="oracle-checking-session" style={{ marginTop: 10, color: C.ivoryD, fontSize: 14 }}>
            Checking member access…
          </div>
        )}

        {/* ── Authenticated member dashboard header ── */}
        {isAuth && (
          <div data-testid="oracle-member-welcome" style={{ marginTop: 10, marginBottom: 18 }}>
            <div style={{ color: C.gold, fontFamily: "'Cinzel', serif", fontSize: 22, fontWeight: 700 }}>
              Welcome back, {displayName}
            </div>
            <div style={{ color: C.ivory, fontSize: 14, marginTop: 2 }} data-testid="member-tier-label">
              {tierLabel} Member · <span style={{ color: C.emerald }}>LOGIX-2 Ready</span>
            </div>
            <div style={S.ctaRow}>
              <Link to="/oracle/dashboard"  style={S.ctaPrimary}    data-testid="cta-continue-to-oracle">Continue to ORACLE</Link>
              <Link to="/oracle/briefings"  style={S.ctaSecondary}  data-testid="cta-my-briefings">My Briefings</Link>
              <Link to="/oracle/watchlists" style={S.ctaSecondary}  data-testid="cta-my-watchlists">My Watchlists</Link>
              <Link to="/oracle/reports"    style={S.ctaSecondary}  data-testid="cta-my-reports">My Reports</Link>
              <Link to="/membership"        style={S.ctaSecondary}  data-testid="cta-my-membership">My Membership</Link>
            </div>
            <div style={S.publicBadgeRow} data-testid="oracle-member-badges">
              <span style={S.publicBadge}     data-testid="badge-oracle-available">● ORACLE Available</span>
              <span style={S.publicBadge}     data-testid="badge-logix2-ready">LOGIX-2 Ready</span>
              <span style={S.publicBadge}     data-testid="badge-member-active">Member Access Active</span>
              <span style={S.publicBadgeMuted} data-testid="badge-public-data">Public Data Mode</span>
            </div>
          </div>
        )}

        {/* ── Session-refresh fallback (token exists but profile fetch failed) ── */}
        {token && memberErr && !memberLoading && (
          <div data-testid="oracle-session-refresh" style={{ marginTop: 14, padding: 12, border: `1px solid ${C.border}`, borderRadius: 8, color: C.ivoryD }}>
            Session needs refresh.{" "}
            <button type="button" onClick={() => navigate("/access")} style={{ ...S.ctaSecondary, height: 38, marginLeft: 8 }} data-testid="cta-signin-again">Sign in again</button>
          </div>
        )}

        {/* ── Anonymous / guest CTAs ── */}
        {!isAuth && !token && !stillChecking && (
          <>
            <div style={S.ctaRow}>
              <button
                type="button"
                onClick={() => navigate("/oracle/health")}
                style={S.ctaPrimary}
                data-testid="cta-primary"
              >Start Free Preview</button>
              <Link to="/access" style={S.ctaSecondary} data-testid="cta-secondary">Sign In</Link>
            </div>
            <div style={S.publicBadgeRow} data-testid="oracle-public-badges">
              <span style={S.publicBadge}     data-testid="badge-oracle-available">● ORACLE Available</span>
              <span style={S.publicBadgeMuted} data-testid="badge-public-data">Public Data Mode</span>
              <span style={S.publicBadgeMuted} data-testid="badge-logix2-signin">LOGIX-2 · Sign in required</span>
              <span style={S.publicBadgeMuted} data-testid="badge-providers-pending">Some live providers pending</span>
            </div>
          </>
        )}
      </header>

      {/* WHAT ORACLE DOES — 8 CARDS */}
      <section style={{ ...S.inner, ...S.section }} data-testid="section-what-oracle-does">
        <h2 style={S.sectionTitle}>What ORACLE Does</h2>
        <div style={S.sectionLead}>Health, Capital, Finance, Sports, Grants, Research, LifeScore™, FoodRx™, and LOGIX-2™ unified through one intelligence operating system.</div>
        <div style={S.cardGrid}>
          {CARDS.map((c) => (
            <Link key={c.title} to={c.to} style={{ ...S.card, textDecoration: "none" }} data-testid={`card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>
              <span style={S.cardIcon} aria-hidden="true">{c.icon}</span>
              <h3 style={S.cardTitle}>{c.title}</h3>
              <p style={S.cardBody}>{c.body}</p>
              <span style={S.cardLink}>Learn more →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* MEMBERSHIP — pricing grid for guests; personalized panel for members */}
      {isAuth ? (
        <section style={{ ...S.inner, ...S.section }} data-testid="section-current-membership">
          <h2 style={S.sectionTitle}>Current Membership: {tierLabel}</h2>
          <div style={S.sectionLead}>Your activated benefits.</div>
          <div style={S.cardGrid} data-testid="member-included-features">
            {[
              "LOGIX-2 Briefings", "LifeScore™ + FoodRx™",
              "Finance & Investment Intelligence", "Sports Intelligence",
              "Grants & Funding", "Research Intelligence", "Reports", "Watchlists",
            ].map((f) => (
              <div key={f} style={S.card}>
                <h3 style={S.cardTitle}>{f}</h3>
                <p style={S.cardBody}>Included with your {tierLabel} membership.</p>
              </div>
            ))}
          </div>
          <div style={{ ...S.ctaRow, marginTop: 18 }}>
            <Link to="/membership"         style={S.ctaPrimary}   data-testid="cta-manage-membership">Manage Membership</Link>
            <Link to="/membership/billing" style={S.ctaSecondary} data-testid="cta-billing">Billing</Link>
          </div>
        </section>
      ) : (
        <section style={{ ...S.inner, ...S.section }} data-testid="section-membership">
        <h2 style={S.sectionTitle}>Membership</h2>
        <div style={S.sectionLead}>Choose a plan. Upgrade or downgrade anytime.</div>
        <div style={S.membershipGrid}>
          {TIERS.map((t) => (
            <div key={t.name} style={t.featured ? S.memCardFeatured : S.memCard} data-testid={`tier-${t.name.toLowerCase()}`}>
              <h3 style={S.memTier}>{t.name}</h3>
              <div>
                <div style={S.memPrice}>{t.price}</div>
                <div style={S.memPriceLine}>{t.suffix}</div>
              </div>
              <ul style={S.memList}>
                {t.features.map((f) => (
                  <li key={f} style={S.memListItem}>
                    <span style={{ position: "absolute", left: 0, color: C.emerald }}>•</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* TRUST & SAFETY */}
      <section style={{ ...S.inner, ...S.section }} data-testid="section-trust-safety">
        <div style={S.trust} data-testid="trust-safety-notice">
          <strong style={{ color: C.gold }}>Trust &amp; Safety.</strong>{" "}
          Research and decision-support only. No autonomous medical, financial, trading, or wagering actions.
          Human confirmation required.
        </div>
      </section>

      <div style={S.footer} data-testid="oracle-public-footer">
        CareLogix™ ORACLE — Multi-Domain Intelligence Command™ · Powered by LOGIX-2™ · Stable Core. Intelligent Satellites. Infinite Expansion.
      </div>
    </main>
  );
}
